import { useCallback, useEffect, useRef, useState } from 'react';

export interface RovingFocusItemProps {
  ref: (el: HTMLElement | null) => void;
  tabIndex: number;
}

export type UseRovingFocusProps = {
  defaultIndex?: number;
  loop?: boolean;
};

export type UseRovingFocusReturn = {
  handleKeyDown: (e: KeyboardEvent) => void;
  getItemProps: (index: number) => RovingFocusItemProps;
};

/**
 * Hook for implementing roving focus pattern in carousels and similar components
 * Follows WCAG guidelines and ARIA Authoring Practices for keyboard navigation
 *
 * @param defaultIndex - Initial focused item index (default: 0)
 * @param loop - Whether navigation should loop around (default: false)
 */
export const useRovingFocus: (props?: UseRovingFocusProps) => UseRovingFocusReturn = ({
  defaultIndex = 0,
  loop = false,
} = {}) => {
  const [currentIndex, setCurrentIndex] = useState(defaultIndex);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  const itemCount = itemRefs?.current?.filter(ref => ref !== null).length ?? 0;

  // Reset index if it's out of bounds
  useEffect(() => {
    if (currentIndex >= itemCount && itemCount > 0) {
      setCurrentIndex(Math.max(0, itemCount - 1));
    }
  }, [currentIndex, itemCount]);

  const setItemRef = useCallback(
    (index: number) => (el: HTMLElement | null) => {
      itemRefs.current[index] = el;
    },
    []
  );

  const focusItem = useCallback(
    (index: number) => {
      if (index < 0 || index >= itemCount) {
        return;
      }

      const item = itemRefs.current[index];

      if (item && document.activeElement !== item) {
        item.focus();
        setCurrentIndex(index);
      }
    },
    [itemCount]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      let newIndex: number;

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown': {
          e.preventDefault();

          if (currentIndex < itemCount - 1) {
            newIndex = currentIndex + 1;
          } else {
            newIndex = loop ? 0 : currentIndex;
          }

          focusItem(newIndex);
          break;
        }
        case 'ArrowLeft':
        case 'ArrowUp': {
          e.preventDefault();

          if (currentIndex > 0) {
            newIndex = currentIndex - 1;
          } else {
            newIndex = loop ? itemCount - 1 : currentIndex;
          }

          focusItem(newIndex);

          break;
        }
        case 'Home': {
          e.preventDefault();
          focusItem(0);
          break;
        }
        case 'End': {
          e.preventDefault();
          focusItem(itemCount - 1);
          break;
        }
        default: {
          break;
        }
      }
    },
    [currentIndex, itemCount, loop, focusItem]
  );

  const getItemProps = useCallback(
    (index: number) => ({
      ref: setItemRef(index),
      tabIndex: currentIndex === index ? 0 : -1,
    }),
    [currentIndex, setItemRef]
  );

  return {
    handleKeyDown,
    getItemProps,
  };
};
